import { useState } from "react"
import { Box, Button, Link, Stack, Typography } from "@mui/material"
import type { PortfolioProject, PortfolioShot } from "./projects"
import { Lightbox } from "./Lightbox"

type Props = {
  project: PortfolioProject
}

function ShotFrame({
  shot,
  onOpen,
}: {
  shot: PortfolioShot
  onOpen: () => void
}) {
  const phone = shot.frame === "phone"
  return (
    <Box
      component="button"
      type="button"
      onClick={onOpen}
      aria-label={`Open ${shot.caption}`}
      sx={{
        display: "block",
        width: "100%",
        minWidth: 0,
        p: 0,
        border: "1px solid rgba(140,190,255,0.28)",
        bgcolor: "rgba(0,0,0,0.45)",
        cursor: "zoom-in",
        textAlign: "left",
        overflow: "hidden",
        "&:hover img": { opacity: 0.92 },
      }}
    >
      <Box
        component="img"
        src={shot.src}
        alt={shot.alt}
        loading="lazy"
        sx={{
          width: "100%",
          height: "auto",
          display: "block",
          aspectRatio: phone ? "9 / 16" : "16 / 10",
          objectFit: phone ? "contain" : "cover",
          objectPosition: "top center",
          bgcolor: phone ? "#050c16" : "transparent",
        }}
      />
      <Typography
        sx={{
          px: 1.25,
          py: 0.85,
          fontSize: "0.62rem",
          letterSpacing: "0.16em",
          color: "rgba(170,200,235,0.75)",
        }}
      >
        {shot.caption}
      </Typography>
    </Box>
  )
}

export function ProjectSection({ project }: Props) {
  const all = [project.primary, ...project.gallery]
  const [lightbox, setLightbox] = useState<number | null>(null)

  return (
    <Box
      id={project.anchor}
      component="section"
      sx={{
        position: "relative",
        zIndex: 2,
        scrollMarginTop: 24,
        px: { xs: 2, md: 4 },
        py: { xs: 8, md: 12 },
        maxWidth: 1100,
        width: "100%",
        mx: "auto",
        pointerEvents: "auto",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Typography
        sx={{
          letterSpacing: "0.24em",
          fontSize: "0.68rem",
          fontWeight: 700,
          color: "rgba(120,230,255,0.85)",
          mb: 1,
        }}
      >
        {project.eyebrow}
      </Typography>
      <Typography
        component="h2"
        sx={{
          fontFamily: '"Space Grotesk", "Sora", system-ui, sans-serif',
          fontWeight: 600,
          letterSpacing: "-0.03em",
          fontSize: { xs: "2rem", md: "3rem" },
          lineHeight: 1.05,
          mb: 1.5,
          wordBreak: "break-word",
        }}
      >
        {project.title}
      </Typography>
      <Typography
        sx={{
          maxWidth: 640,
          color: "rgba(210,225,255,0.8)",
          fontSize: { xs: "0.98rem", md: "1.05rem" },
          lineHeight: 1.55,
          mb: 3,
        }}
      >
        {project.description}
      </Typography>

      <Box sx={{ mb: 3, minWidth: 0 }}>
        <ShotFrame shot={project.primary} onOpen={() => setLightbox(0)} />
      </Box>

      {project.demoVideo ? (
        <Box
          sx={{
            mb: 3,
            border: "1px solid rgba(140,190,255,0.28)",
            bgcolor: "rgba(0,0,0,0.55)",
            overflow: "hidden",
            maxWidth: "100%",
          }}
        >
          <Box
            component="video"
            controls
            playsInline
            preload="metadata"
            poster={project.demoVideo.poster}
            title={project.demoVideo.title}
            sx={{
              display: "block",
              width: "100%",
              height: "auto",
              aspectRatio: "16 / 9",
              objectFit: "cover",
              bgcolor: "#000",
            }}
          >
            {project.demoVideo.srcWebm ? (
              <source src={project.demoVideo.srcWebm} type="video/webm" />
            ) : null}
            <source src={project.demoVideo.src} type="video/mp4" />
          </Box>
          {project.demoVideo.caption ? (
            <Typography
              sx={{
                px: 1.25,
                py: 0.85,
                fontSize: "0.62rem",
                letterSpacing: "0.16em",
                color: "rgba(170,200,235,0.75)",
              }}
            >
              {project.demoVideo.caption}
            </Typography>
          ) : null}
        </Box>
      ) : project.youtubeVideoId ? (
        <Box
          sx={{
            mb: 3,
            border: "1px solid rgba(140,190,255,0.28)",
            bgcolor: "rgba(0,0,0,0.55)",
            overflow: "hidden",
            aspectRatio: "16 / 9",
            maxWidth: "100%",
          }}
        >
          <Box
            component="iframe"
            title={project.youtubeTitle ?? `${project.title} video`}
            src={`https://www.youtube.com/embed/${project.youtubeVideoId}`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            sx={{
              display: "block",
              width: "100%",
              height: "100%",
              border: 0,
            }}
          />
        </Box>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
          gap: 1.5,
          mb: 3,
          minWidth: 0,
        }}
      >
        {project.gallery.map((shot, i) => (
          <ShotFrame
            key={shot.src}
            shot={shot}
            onOpen={() => setLightbox(i + 1)}
          />
        ))}
      </Box>

      <Stack spacing={2} sx={{ mb: 3, minWidth: 0 }}>
        <Box>
          <Typography
            sx={{
              letterSpacing: "0.18em",
              fontSize: "0.62rem",
              fontWeight: 700,
              color: "rgba(160,200,255,0.7)",
              mb: 1,
            }}
          >
            CAPABILITIES
          </Typography>
          <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.25 }}>
            {project.capabilities.map((c) => (
              <Typography
                component="li"
                key={c}
                sx={{ color: "rgba(210,225,255,0.82)", fontSize: "0.9rem" }}
              >
                {c}
              </Typography>
            ))}
          </Stack>
        </Box>
        <Typography sx={{ color: "rgba(170,195,230,0.65)", fontSize: "0.78rem" }}>
          {project.tech.join(" · ")}
        </Typography>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} useFlexGap sx={{ flexWrap: "wrap" }}>
        {project.href ? (
          <Button
            component={Link}
            href={project.href}
            target="_blank"
            rel="noreferrer"
            variant="contained"
            sx={{
              px: 2.5,
              py: 1,
              borderRadius: 999,
              letterSpacing: "0.14em",
              fontSize: "0.72rem",
              fontWeight: 700,
              bgcolor: "rgba(0,180,255,0.9)",
              color: "#041018",
              textDecoration: "none",
              "&:hover": { bgcolor: "rgba(120,220,255,1)" },
            }}
          >
            {project.hrefLabel ?? "OPEN PROJECT"}
          </Button>
        ) : (
          <Typography
            sx={{
              letterSpacing: "0.14em",
              fontSize: "0.68rem",
              color: "rgba(160,190,230,0.55)",
              alignSelf: "center",
            }}
          >
            {project.hrefLabel ?? "PRIVATE / LOCAL PRODUCT"}
          </Typography>
        )}
        {project.youtubeVideoId ? (
          <Button
            component={Link}
            href={`https://www.youtube.com/watch?v=${project.youtubeVideoId}`}
            target="_blank"
            rel="noreferrer"
            sx={{
              px: 2.5,
              py: 1,
              borderRadius: 999,
              letterSpacing: "0.14em",
              fontSize: "0.72rem",
              fontWeight: 700,
              color: "rgba(200,220,255,0.95)",
              border: "1px solid rgba(140,170,210,0.45)",
              textDecoration: "none",
              "&:hover": {
                bgcolor: "rgba(20,40,70,0.7)",
                borderColor: "rgba(160,210,255,0.7)",
              },
            }}
          >
            WATCH ON YOUTUBE
          </Button>
        ) : null}
      </Stack>

      {lightbox !== null ? (
        <Lightbox
          shots={all}
          index={lightbox}
          onClose={() => setLightbox(null)}
          onPrev={() =>
            setLightbox((i) => (i === null ? 0 : (i + all.length - 1) % all.length))
          }
          onNext={() => setLightbox((i) => (i === null ? 0 : (i + 1) % all.length))}
        />
      ) : null}
    </Box>
  )
}
